import { createElement, Fragment, type ReactNode } from "react"
import { AccentSync } from "@/components/AccentSync"
import { FeedbackWidget } from "@/components/FeedbackWidget"
import { FlashToaster, ToastProvider } from "@/components/Toaster"
import { I18nProvider } from "@/lib/i18n"

type PageComponent = {
  layout?: ((page: ReactNode) => ReactNode) | React.ComponentType<{ children: ReactNode }>[]
}

/**
 * Inertia's default page rendering (page + optional layouts) plus the global
 * feedback dialog, the accent-color sync, toasts for flash messages, and the
 * react-intl provider. Rendering it here, inside <App>, lets the dialog read the
 * shared page props (e.g. whether someone is signed in). Used by both the
 * client and SSR entrypoints so they produce the same tree.
 */
export function renderPage({
  Component,
  props,
  key,
}: {
  Component: unknown
  props: Record<string, unknown>
  key: number | null
}): ReactNode {
  const component = Component as PageComponent & React.ComponentType<Record<string, unknown>>
  const child = createElement(component, { key, ...props })

  let page: ReactNode = child
  if (typeof component.layout === "function") {
    page = component.layout(child)
  } else if (Array.isArray(component.layout)) {
    const layouts = [...component.layout].reverse()
    page = layouts.reduce<ReactNode>((children, Layout) => createElement(Layout, { children, ...props }), child)
  }

  return createElement(
    I18nProvider,
    { locale: props.locale as string | undefined, messages: props.catalog as Record<string, string> | null | undefined },
    createElement(
      ToastProvider,
      null,
      createElement(Fragment, null, page, createElement(FeedbackWidget), createElement(AccentSync), createElement(FlashToaster)),
    ),
  )
}
